import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { BUSINESS } from "@/lib/constants";

/**
 * Digital Receptionist — premium asymmetrical intake section.
 *
 * On-brand palette: dark navy + cyan + yellow + Oswald display.
 * Asymmetric 7/5 grid (NOT a 3-column template).
 * Left  — magazine-style editorial detailing the 5-tier priority queue.
 * Right — glassmorphism multi-step card capturing Name, Phone, Lanai Project Notes.
 *
 * Wires to the existing /api/leads/estimate endpoint so spam protection,
 * the urgency classifier, and the Make.com dry-run notification path all
 * apply unchanged.
 */

const PRIORITY_TIERS = [
    {
        code: "P1",
        label: "Storm-active",
        eta: "Same business day",
        desc: "Tree limb through the cage, panels actively flapping in wind, water entering the lanai. Routed straight to the on-call line.",
    },
    {
        code: "P2",
        label: "Acute leak / safety",
        eta: "≤ 24 hours",
        desc: "Pet escape risk, exposed pool, large screen failure that won't last the weekend. Triaged before regular intake.",
    },
    {
        code: "P3",
        label: "HOA deadline",
        eta: "≤ 48 hours",
        desc: "Documented written notice or compliance window from your HOA / property manager. We aim for an on-site reading early.",
    },
    {
        code: "P4",
        label: "Estimate request",
        eta: "Next business window",
        desc: "Standard rescreen, panel replacement, or screen door work. We confirm scope and schedule a no-charge measurement visit.",
    },
    {
        code: "P5",
        label: "Routine / planning",
        eta: "This week",
        desc: "You're collecting options, planning a season project, or comparing scope. We respond by your preferred channel — no pressure.",
    },
];

const STEPS = [
    { id: "name", label: "Contact" },
    { id: "phone", label: "Phone" },
    { id: "notes", label: "Lanai notes" },
];

export default function DigitalReceptionistSection() {
    const [step, setStep] = useState(0);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [notes, setNotes] = useState("");
    const [consent, setConsent] = useState(false);

    const [honeypot, setHoneypot] = useState("");
    const startMs = useRef(Date.now());

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [touchedFirst, setTouchedFirst] = useState(false);

    useEffect(() => {
        startMs.current = Date.now();
    }, []);

    const progressPct = useMemo(() => {
        if (STEPS.length <= 1) return 100;
        return (step / (STEPS.length - 1)) * 100;
    }, [step]);

    const canAdvance = useMemo(() => {
        if (step === 0) return name.trim().length >= 2;
        if (step === 1) return phone.replace(/\D/g, "").length >= 7;
        return consent && phone.replace(/\D/g, "").length >= 7;
    }, [step, name, phone, consent]);

    const handleFirstFocus = () => {
        if (!touchedFirst) {
            setTouchedFirst(true);
            track(EVENTS.ESTIMATE_START, { variant: "digital-receptionist", source: "/digital-receptionist" });
        }
    };

    const goNext = () => {
        setError(null);
        if (!canAdvance) return;
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };
    const goBack = () => {
        setError(null);
        setStep((s) => Math.max(s - 1, 0));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!phone.trim()) {
            setError("Please share a phone number so we can follow up.");
            return;
        }
        if (!consent) {
            setError("Please confirm you'd like A1 Screening to contact you about your project.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: name.trim() || null,
                phone: phone.trim(),
                email: null,
                city: "Naples",
                address: null,
                service_needed: "pool-cage-repair",
                project_type: null,
                urgency_input: null,
                notes: notes.trim() || null,
                preferred_contact: null,
                best_time: null,
                consent_given: true,
                website_url: honeypot,
                start_ms: startMs.current,
            };
            const res = await api.post("/leads/estimate", payload);
            track(EVENTS.ESTIMATE_SUBMIT, {
                variant: "digital-receptionist",
                source: "/digital-receptionist",
                lead_id: res.data?.lead_id,
            });
            setSuccess(res.data.confirmation_message);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Sorry — something went wrong. Please try again or call 239-434-7306.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="dr-section" data-testid="digital-receptionist-section">
            <div className="a1-container a1-section">
                <div className="dr-grid">
                    {/* ============== LEFT — EDITORIAL ============== */}
                    <div className="a1-fade-up" data-testid="dr-editorial">
                        <div className="dr-kicker" data-testid="dr-kicker">
                            Priority queuing · Operations desk
                        </div>

                        <h1 className="dr-headline" data-testid="dr-headline">
                            Your request never waits in line behind a{" "}
                            <span className="dr-headline-accent">missed call.</span>
                        </h1>

                        <p className="dr-lede" data-testid="dr-lede">
                            Every inbound message — phone, text, web form, or storm-line ping — is
                            sorted into one of five priority tiers the moment it lands. Naples lanais
                            don't wait politely, and neither does our queue.
                        </p>

                        <ol className="dr-tier-list" data-testid="dr-tier-list">
                            {PRIORITY_TIERS.map((tier) => (
                                <li
                                    key={tier.code}
                                    className="dr-tier"
                                    data-testid={`dr-tier-${tier.code.toLowerCase()}`}
                                >
                                    <span className="dr-tier-num">{tier.code}</span>
                                    <div>
                                        <div className="dr-tier-label">{tier.label}</div>
                                        <div className="dr-tier-desc">{tier.desc}</div>
                                    </div>
                                    <span className="dr-tier-eta">{tier.eta}</span>
                                </li>
                            ))}
                        </ol>

                        <blockquote className="dr-pullquote" data-testid="dr-pullquote">
                            "If it's leaking on someone's lanai right now, it jumps the line.
                            Estimates are routed for technician review the same business day they
                            come in."
                        </blockquote>
                        <p className="dr-byline">— John Stephens, Owner · A1 Screening Services</p>
                    </div>

                    {/* ============== RIGHT — GLASSMORPHIC MULTI-STEP CARD ============== */}
                    <div className="dr-card-wrap">
                        <div className="dr-card a1-fade-up-delay-1" data-testid="dr-card">
                            <div className="dr-card-eyebrow">Digital receptionist · {BUSINESS.hours}</div>
                            <h2 className="dr-card-title" data-testid="dr-card-title">
                                Tell us what your lanai needs.
                            </h2>
                            <p className="dr-card-sub">
                                Three short steps. We route to technician review the same business day.
                            </p>

                            {/* Stepper */}
                            <div className="dr-stepper" data-testid="dr-stepper" aria-hidden="true">
                                {STEPS.map((s, i) => (
                                    <div
                                        key={s.id}
                                        style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : "0 0 auto" }}
                                    >
                                        <span
                                            className={`dr-step-dot ${
                                                i === step ? "is-active" : i < step ? "is-done" : ""
                                            }`}
                                            data-testid={`dr-step-dot-${i}`}
                                        >
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {i < STEPS.length - 1 ? (
                                            <span
                                                className="dr-step-line"
                                                style={{
                                                    "--progress": i < step ? "100%" : i === step ? "50%" : "0%",
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                            <div className="dr-step-label" data-testid="dr-step-label">
                                Step {step + 1} of {STEPS.length} · {STEPS[step].label}
                            </div>

                            {success ? (
                                <div className="dr-success" data-testid="dr-success">
                                    <div className="dr-success-mark">✓</div>
                                    <h3 className="dr-success-title">Received.</h3>
                                    <p className="dr-success-text">{success}</p>
                                </div>
                            ) : (
                                <form onSubmit={submit} noValidate data-testid="dr-form" style={{ marginTop: "1.1rem" }}>
                                    <div className="dr-step-stack">
                                        {/* STEP 1 — NAME */}
                                        <div className={`dr-step-panel ${step === 0 ? "is-active" : ""}`}>
                                            <div className="dr-field">
                                                <label className="dr-field-label" htmlFor="dr-name">
                                                    Your name
                                                </label>
                                                <input
                                                    id="dr-name"
                                                    type="text"
                                                    className="dr-input"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    onFocus={handleFirstFocus}
                                                    placeholder="First and last"
                                                    autoComplete="name"
                                                    data-testid="dr-input-name"
                                                />
                                            </div>
                                        </div>

                                        {/* STEP 2 — PHONE */}
                                        <div className={`dr-step-panel ${step === 1 ? "is-active" : ""}`}>
                                            <div className="dr-field">
                                                <label className="dr-field-label" htmlFor="dr-phone">
                                                    Best phone number
                                                </label>
                                                <input
                                                    id="dr-phone"
                                                    type="tel"
                                                    inputMode="tel"
                                                    className="dr-input"
                                                    value={phone}
                                                    onChange={(e) => setPhone(e.target.value)}
                                                    onFocus={handleFirstFocus}
                                                    placeholder="(239) 555-0101"
                                                    autoComplete="tel"
                                                    data-testid="dr-input-phone"
                                                />
                                            </div>
                                        </div>

                                        {/* STEP 3 — LANAI NOTES */}
                                        <div className={`dr-step-panel ${step === 2 ? "is-active" : ""}`}>
                                            <div className="dr-field">
                                                <label className="dr-field-label" htmlFor="dr-notes">
                                                    Lanai project notes (optional)
                                                </label>
                                                <textarea
                                                    id="dr-notes"
                                                    className="dr-textarea"
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    onFocus={handleFirstFocus}
                                                    placeholder="Tell us what you've noticed — panel count, where it's torn, HOA deadline, storm damage, etc."
                                                    data-testid="dr-input-notes"
                                                />
                                            </div>

                                            <label className="dr-consent" data-testid="dr-consent-label">
                                                <input
                                                    type="checkbox"
                                                    checked={consent}
                                                    onChange={(e) => setConsent(e.target.checked)}
                                                    required
                                                    aria-required="true"
                                                    data-testid="dr-consent-checkbox"
                                                />
                                                <span>
                                                    By submitting, you agree that A1 Screening may contact you by
                                                    phone, text, or email about your project. Message and data rates
                                                    may apply. Reply STOP to opt out of texts. See our{" "}
                                                    <a href="/privacy-policy">Privacy Policy</a> and{" "}
                                                    <a href="/terms">Terms</a>.
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    {error ? (
                                        <div className="dr-error" role="alert" data-testid="dr-error">
                                            {error}
                                        </div>
                                    ) : null}

                                    {/* Honeypot */}
                                    <input
                                        type="text"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        aria-hidden="true"
                                        className="dr-honeypot"
                                        value={honeypot}
                                        onChange={(e) => setHoneypot(e.target.value)}
                                        name="website_url"
                                    />

                                    <div className="dr-btn-row">
                                        {step > 0 ? (
                                            <button
                                                type="button"
                                                className="dr-btn dr-btn-ghost"
                                                onClick={goBack}
                                                data-testid="dr-back-btn"
                                            >
                                                ← Back
                                            </button>
                                        ) : null}
                                        {step < STEPS.length - 1 ? (
                                            <button
                                                type="button"
                                                className="dr-btn"
                                                onClick={goNext}
                                                disabled={!canAdvance}
                                                data-testid="dr-next-btn"
                                            >
                                                Continue →
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                className="dr-btn"
                                                disabled={submitting || !canAdvance}
                                                data-testid="dr-submit-btn"
                                            >
                                                {submitting ? "Sending…" : "Route my request"}
                                            </button>
                                        )}
                                    </div>

                                    <p className="dr-helper" data-testid="dr-helper">
                                        Prefer to talk? Call{" "}
                                        <a href={`tel:${BUSINESS.phoneTel}`} style={{ color: "var(--color-accent)" }}>
                                            {BUSINESS.phone}
                                        </a>{" "}
                                        · {BUSINESS.hours}.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
